/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { animated, SpringValue, useSpring } from '@react-spring/web';
import { Tooltip } from 'radix-ui';
import React, { memo, ReactNode, useCallback, useMemo } from 'react';

import { HorseColor, HorseColorValues } from '~/app';
import { useOnStateChange } from '~/utils';
import { cn } from '~/utils/styles';

interface Props {
  children?: ReactNode;
  color: HorseColor;
  selected?: boolean;
  selectedColor?: string;
  isDropped?: boolean;
  droppedColor?: string;
  isPreview?: boolean;
  previewColor?: string;
  orientation?: HorseOrientation;
  className?: string;
  nextTimeoutDateString?: string;
  style?: React.CSSProperties;
  rotation?: SpringValue<number>;
  onClick?: () => void;
  onOpenToolTip?: () => void;
}

export type HorseOrientation = 'l' | 'r';

const progressRadius = 47;
const progressDasharray = Math.PI * (progressRadius * 2);
const progressAnimation = {
  from: { strokeDashoffset: 0 },
  strokeDashoffset: progressDasharray,
  reset: true,
};

const HorseIcon = ({
  children,
  color,
  selected = false,
  selectedColor: _selectedColor = '#fff',
  isDropped = false,
  droppedColor = '#fff',
  isPreview = false,
  previewColor = '#fff',
  orientation = 'r',
  nextTimeoutDateString,
  rotation,
  className = '',
  onOpenToolTip,
  ...props
}: Props) => {
  const [, animatedProgressPropsApi] = useSpring(
    () => ({
      from: { strokeDashoffset: 0 },
    }),
    [],
  );

  const duration = useMemo(() => {
    if (!selected || !nextTimeoutDateString) return undefined;
    const newNextTimeoutDate = new Date(nextTimeoutDateString);
    if (isNaN(newNextTimeoutDate.getTime())) return undefined;
    const duration = newNextTimeoutDate.getTime() - new Date().getTime();
    if (duration > 0) return duration;
    return undefined;
  }, [nextTimeoutDateString, selected]);

  useOnStateChange(
    duration,
    useCallback(
      (duration) => {
        if (!duration || duration <= 0) {
          animatedProgressPropsApi.start({ strokeDashoffset: 0, immediate: true });
        } else {
          animatedProgressPropsApi.start({
            ...progressAnimation,
            config: { duration },
          });
        }
      },
      [animatedProgressPropsApi],
    ),
  );

  return (
    <Tooltip.TooltipProvider>
      <Tooltip.Root
        delayDuration={0}
        onOpenChange={(open) => {
          if (open) onOpenToolTip?.();
        }}
      >
        <Tooltip.Trigger asChild>
          <div className={cn('relative', className)} {...props}>
            <animated.svg
              baseProfile="basic"
              viewBox="0 0 500 500"
              className="absolute inset-0 h-full w-full"
              transform={rotation ? rotation.to((rotation) => `rotate(${rotation})`) : ''}
            >
              <circle
                opacity={isDropped || isPreview ? 0.3 : 1}
                cx={250}
                cy={250}
                r={250}
                fillRule="evenodd"
                clipRule="evenodd"
                fill={HorseColorValues[color]}
              />
              <path
                opacity={isDropped || isPreview ? 0.3 : 1}
                d="M341.2 76.4c-15.2-3.1-27.7 2.5-26.7 19-1.9.3-3.1 1.9-4.9 2.3-2.6-.2-5.2-.5-7.8-.7-5 .6-9.3 3.8-13.9 5.1-7.5 2.1-14.2 3.4-20.7 7.2-3.6 2.1-5.3 6.8-8.3 10.1-4.5 5.1-15.7 7-16.1 18.9-.2 6.8 2.6 13.1 5.9 17.2 2.3 2.9 5.7 4 5.3 9.2-2 2.3-3.4 7.9-4.6 11.1-.2 0-.4-.1-.6-.1-3.5-4.5-13-4.6-17.6-8-6.3-4.6-12.9-10.3-20.9-14-10.4-4.8-29.3 8.3-37.2 11.7-8.2 3.5-34.8 3.8-45.7 2-17.7-3-31.6-9.8-48.6-9.8-14.8 0-17.5-4.8-30.1 1.2-2.3 1.1-5.1 1.8-6.9 3.8 1.7 6.1 1 13.3 2.6 19.5 1.3 5.2 5.4 8.5 8.3 12.7 1.5 2.3 1 5 1.8 7.9l9.1 13.1c7.7 6.8 21 2.3 28.1.3 3.7-.4 7.5-.8 11.2-1.1.7.4 1.5.8 2.2 1.2 1.4-.4 3.8-3.1 5.5-3.6 10.8-3.3 24.2-8.1 36.5-6.5-.1.5-.3 1-.4 1.4 1.5 5.7-1 13.7.6 19.2 3.1 10.4 12 21.3 13.4 33.1 1 8.4-7 26.7-4.6 32.1 2.3 8.8 11.4 12.7 15.9 19.8 4.4 6.8.5 21.1 5.1 28 4.9 7.4 11.7 12 17 19.2 2.8 3.8 2.4 12.2 4 17.2 2.6 5.4 5.2 10.9 7.8 16.4 1.8 2.6 4.5 3.5 5.7 6.7 1.1 3.4-1.6 10.5-.6 14.3 3.2 7.2 26.7 13.5 33.8 8 2.7-2.8 4.2-8.4 5.3-14.5-2.8-3.9-2.7-10-4.4-15-2.7-8.1-12.1-20.3-11.9-29.7 0 0 1.5-26.2 1-27.4-1-2.5-9.9-6.4-12.5-8.3-5.2-3.9-23.9-25.4-23-33-.8-2.8 1.6-7.5 1.8-11.3.6-11.9 1.6-30.7 12.3-33.2 7.4-4.4 19.8 1.3 22 7.8 1 3 .4 6.1 2.2 8.3 3.2 3.9 9.2 8 13.5 10.4 4.2.7 8.3 1.5 12.5 2.2 3 1.9 4.5 6.8 6.8 9.3 3.1 3.4 9.4 2.5 13.2 2.3.9-4.8 2.6-7.2 5.8-7.9 3.3-2.3 8.1 1 12.7.6 13.2-1 28.9 10.3 29.5 23.9-2.3-.4-4.6-.8-7-1.2-2.1-2.2-5.9-.1-9.5-1.7-12.6-5.6-18.5 5.9-29 7.4-1.3-3.3-5.3-9.6-8-11.4-3.5 2.5-7.8 3.1-11.3 5.5-5.1 3.4-8.5 11.2-13.7 14.5.2.3.3.6.5.9 4.7 12.5 23.6 14.5 39 17.2 5.7 1 12.6 3.4 17.6 2.2 2.7-.7 3.3-4.1 5.3-5.1 1.9-.9 8.4-1.4 10.1-1.7 7.7-1.2 14.2 0 22.8 1.5 2.3-3.4 6.4-4.5 8.7-7.8.9-2.1 1.7-4.2 2.6-6.3 1.5.3 3 .5 4.5.8 1.1 1.5 3.1 2.5 4.9 3.4-.1.5-.1 1.1-.2 1.7-.2 0-.4-.1-.6-.1-3.8 5.3-9.8 6.6-14.8 10.1-4.7 3.3-8.1 13.6-12.5 16.8-2 1.5-4.4.5-6.8 1.4-4.1 1.5-9.4 4-13.5 6.4-6.5 3.8-10.7 11.9-17 15.9.2.3.4.7.5 1 3.4 4.8 8.9 6.2 12.9 10.5 6.3 6.8 6.6 20.2 17 23.5 6-4.4 9.4-1.1 10.6-14.9 1.8-3 1.6-9.6 3.4-12.2 3.5-3.2 8.6-3 11.5-7 7.6-10.2 14.9-21.5 24-28.4 6.8-5.1 13.8-8.6 15-22.9-3.3-6.9-3.5-14.1-8-19.6-7.5-9-28.8-28.4-28.9-41.7 4.9-7.4 1.8-21.6 5.3-31.7 3.2-9.3 10.2-11.1 15.1-17.2 3-3.7 5.5-10 9.5-12 1.8.3 3.5.6 5.3.9 2.5 2.4 6.3 1.5 9.9 3.1 4.5 2.1 10.4 4.5 14.4 6.8 3.8 2.1 9.8 1.3 13 3 2 1.1 10 1.8 10.1 1.7 10-1.9 11.9-26.2 7.2-37.8-.1-.3-9.7-11.9-10.3-12.5-2.2-2.1-5.1-2.5-7.2-4.6-1.2-1.8-2.5-3.5-3.7-5.3-4.1-4.3-8.4-6.8-13-10.7-1.7-2.7-3.4-5.3-5.1-8-1.4-1.4-2.7-1.3-4.3-2.7-2.2-1.9-3.9-6-6.1-8.1-2.7-2.6-14.5-9.2-18.6-6.3-5.6 2.9-9.7 9.2-15.5 12-3.7 1.8-7.6 1.3-11.7 2.5-4.8 1.4-11.4 7.1-14.7 11.3-.2-.2-.3-.5-.5-.7-6.7-6.5-11.6-12-12-24.7.4.1.7.1 1.1.2 2-1.2 5.1-2 7.7-1.9.7 1.1 2.5 3 3.1 2.4 3.4-3 5.4-7.6 8.3-11.2 6.2-7.7 10-5.2 14-18-6.2-2.8-8.9-9-13.3-13.1-3.1-1.8-6.3-3.7-9.5-5.7z"
                fillRule="evenodd"
                clipRule="evenodd"
                fill="#fff"
                transform={orientation === 'l' ? 'scale(-1 1) translate(-500)' : ''}
              />
              {isPreview && (
                <svg
                  version="1.1"
                  viewBox="0 0 512.012 512.012"
                  width="300"
                  height="300"
                  x="100"
                  y="100"
                >
                  <path
                    d="M314.899,87.007L314.899,87.007c-22.701-4.887-46.395,0.751-64.461,15.34c-18.066,14.589-28.569,36.563-28.572,59.785
				c-0.017,9.156,4.859,17.624,12.785,22.207c7.927,4.583,17.698,4.583,25.625,0c7.927-4.583,12.802-13.051,12.785-22.207
				c0.002-7.812,3.564-15.196,9.676-20.061c6.112-4.865,14.107-6.68,21.72-4.93c9.607,2.085,17.108,9.592,19.188,19.2
				c1.581,7.079,0.13,14.496-4,20.458c-4.044,5.78-10.289,9.641-17.267,10.675c-16.768,2.444-29.231,16.78-29.317,33.725v26.275
				c0,14.138,11.461,25.6,25.6,25.6c14.138,0,25.6-11.461,25.6-25.6v-12.925c32.401-11.446,53.262-43.015,51.086-77.31
				S348.487,94.265,314.899,87.007z M347.599,196.382L347.599,196.382c-8.171,11.66-20.227,20.03-34.009,23.608
				c-3.766,0.972-6.397,4.369-6.396,8.258v19.225c0,4.713-3.821,8.533-8.533,8.533c-4.713,0-8.533-3.82-8.533-8.533v-26.275
				c0.048-8.466,6.286-15.622,14.667-16.825c11.746-1.721,22.249-8.248,28.993-18.019c6.744-9.77,9.122-21.905,6.566-33.498
				c-3.515-16.227-16.187-28.904-32.413-32.425c-3.088-0.656-6.235-0.988-9.392-0.992c-9.765-0.024-19.233,3.352-26.779,9.55
				c-9.996,8.096-15.795,20.278-15.775,33.142c0.01,3.054-1.614,5.881-4.258,7.411c-2.644,1.53-5.903,1.53-8.547,0
				s-4.267-4.356-4.258-7.411c-0.033-18.001,8.083-35.05,22.075-46.375c23.122-18.817,56.58-17.702,78.398,2.613
				S364.72,171.977,347.599,196.382z"
                    fill={previewColor}
                  />
                  <path
                    d="M298.666,290.14c-14.138,0-25.6,11.461-25.6,25.6s11.461,25.6,25.6,25.6s25.6-11.462,25.6-25.6
				C324.251,301.608,312.798,290.155,298.666,290.14z M298.666,324.273c-4.713,0-8.533-3.82-8.533-8.533s3.82-8.533,8.533-8.533
				s8.533,3.82,8.533,8.533C307.195,320.451,303.377,324.269,298.666,324.273z"
                    fill={previewColor}
                  />
                  <path
                    d="M500.554,144.366C471.064,58.016,389.913-0.011,298.666,0.007C220.14-0.045,147.942,43.075,110.754,112.238
				c-37.188,69.163-33.344,153.169,10.003,218.647l-35.392,35.391l-0.008,0.005l-0.005,0.008l-30.198,30.197l-0.005,0.003
				l-0.003,0.005l-42.664,42.662c-16.651,16.673-16.642,43.685,0.02,60.347c16.662,16.662,43.674,16.671,60.347,0.02l42.662-42.663
				l0.005-0.003l0.003-0.005l30.197-30.198l0.008-0.005l0.005-0.008l35.391-35.392c76.128,50.304,175.823,46.611,248.02-9.188
				S530.043,230.716,500.554,144.366z M60.783,487.457c-10.15,9.65-26.083,9.65-36.233,0c-9.983-10.015-9.983-26.219,0-36.233
				l36.634-36.633l36.233,36.233L60.783,487.457z M109.482,438.756L73.25,402.524l18.142-18.142l36.231,36.231L109.482,438.756z
				 M139.689,408.547l-36.23-36.23l27.438-27.438c10.59,13.477,22.753,25.639,36.229,36.229L139.689,408.547z M298.666,409.607
				c-108.395,0-196.267-87.872-196.267-196.267S190.271,17.073,298.666,17.073s196.267,87.872,196.267,196.267
				C494.811,321.685,407.011,409.485,298.666,409.607z"
                    fill={previewColor}
                  />
                  <path
                    d="M298.666,34.14c-98.969,0-179.2,80.231-179.2,179.2s80.231,179.2,179.2,179.2s179.2-80.231,179.2-179.2
				C477.754,114.417,397.589,34.252,298.666,34.14z M298.666,375.473c-89.544,0-162.133-72.59-162.133-162.133
				s72.59-162.133,162.133-162.133s162.133,72.59,162.133,162.133C460.698,302.842,388.168,375.372,298.666,375.473z"
                    fill={previewColor}
                  />
                </svg>
              )}
              {isDropped && (
                <svg version="1.1" id="Capa_1" viewBox="0 0 52 52">
                  <path
                    d="M44.385,7.615C39.475,2.705,32.945,0,26,0S12.525,2.705,7.615,7.615S0,19.055,0,26s2.705,13.475,7.615,18.385
	S19.055,52,26,52s13.475-2.705,18.385-7.615S52,32.945,52,26S49.295,12.525,44.385,7.615z M9.029,9.029C13.562,4.496,19.589,2,26,2
	c5.739,0,11.167,2.006,15.494,5.677L7.677,41.494C4.006,37.167,2,31.74,2,26C2,19.589,4.496,13.563,9.029,9.029z M42.971,42.971
	C38.437,47.504,32.411,50,26,50c-5.739,0-11.167-2.006-15.494-5.677l33.817-33.817C47.994,14.833,50,20.26,50,26
    C50,32.411,47.504,38.438,42.971,42.971z"
                    fill={droppedColor}
                  />
                </svg>
              )}
            </animated.svg>

            {selected && (
              <>
                {/* Animated white ring */}
                <svg
                  baseProfile="basic"
                  viewBox="0 0 100 100"
                  className={cn(
                    'absolute inset-0 h-full w-full scale-110 transition-all duration-300',
                    selected ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <circle
                    cx={50}
                    cy={50}
                    r={47}
                    stroke="white"
                    strokeWidth="6"
                    fill="none"
                    className="opacity-50"
                  />
                  {/* Subtle yellow ring */}
                  <circle
                    cx={50}
                    cy={50}
                    r={44}
                    stroke="#fde047"
                    strokeWidth="4"
                    fill="none"
                    className="opacity-30"
                  />
                  {/* Translucent white background */}
                  <circle cx={50} cy={50} r={47} fill="#fff" className="opacity-10" />
                </svg>
              </>
            )}
          </div>
        </Tooltip.Trigger>

        {children ? (
          <Tooltip.Portal>
            <Tooltip.Content
              sideOffset={16}
              className="max-w-[400px] min-w-[320px] rounded-lg border border-gray-100 bg-white px-2 py-2 font-[16px] shadow-xl"
            >
              {children}
              <Tooltip.Arrow className="fill-white" />
            </Tooltip.Content>
          </Tooltip.Portal>
        ) : null}
      </Tooltip.Root>
    </Tooltip.TooltipProvider>
  );
};

export default memo(HorseIcon);
